import type { ClientServiceProfileFragmentFragment } from "@/__generated__/graphql";
import { ClientServiceProfileFragmentFragmentDoc } from "@/__generated__/graphql";
import { ClientInstance } from "@/lib/graphql/apollo";
import type { OptionType } from "@/types/form";

import type { PetProfileFormData } from "./usePetProfileForm";

export const petConditionOptions: OptionType[] = [
  { value: "senior-pet", label: "Senior pet (over 10 years old)" },
  {
    value: "not-fully-vaccinated",
    label: "Not fully vaccinated or vaccination status unknown",
  },
  {
    value: "has-existing-health-conditions",
    label: "Has existing health conditions",
  },
  { value: "pregnant", label: "Pregnant" },
  {
    value: "aggressive-or-highly-anxious",
    label: "Aggressive or highly anxious",
  },
];

export const petConditionValues = petConditionOptions.map((option) => ({
  value: option.value,
  selected: false,
}));

export const petConditionKeys = petConditionOptions.map((option) => option.value);

const getPetTypeFromAttributes = (
  profileAttributes: Record<string, unknown>,
): "dog" | "cat" | null => {
  const type = profileAttributes.type;
  if (type === "dog" || type === "cat") {
    return type;
  }
  return null;
};

export const validatePetProfile = (
  data: PetProfileFormData,
  profiles: Array<{ id: string }>,
  isEdit: boolean,
  currentProfile?: ClientServiceProfileFragmentFragment,
): string | null => {
  const newPetType = data.type;

  if (!newPetType) {
    return "Pet type is required";
  }

  let dogCount = 0;
  let catCount = 0;

  profiles.forEach((profileItem) => {
    if (isEdit && currentProfile && profileItem.id === currentProfile.id) {
      return;
    }

    const profile = ClientInstance.readFragment({
      id: `ClientServiceProfile:${profileItem.id}`,
      fragment: ClientServiceProfileFragmentFragmentDoc,
    });

    if (profile) {
      const petType = getPetTypeFromAttributes(
        profile.profileAttributes as Record<string, unknown>,
      );
      if (petType === "dog") {
        dogCount++;
      } else if (petType === "cat") {
        catCount++;
      }
    }
  });

  if (isEdit && currentProfile) {
    const currentPetType = getPetTypeFromAttributes(
      currentProfile.profileAttributes as Record<string, unknown>,
    );

    if (currentPetType && currentPetType !== newPetType) {
      if (newPetType === "dog" && dogCount >= 3) {
        return "You can have up to 3 dog profiles";
      }
      if (newPetType === "cat" && catCount >= 3) {
        return "You can have up to 3 cat profiles";
      }
    }
  } else {
    if (newPetType === "dog" && dogCount >= 3) {
      return "You can have up to 3 dog profiles";
    }
    if (newPetType === "cat" && catCount >= 3) {
      return "You can have up to 3 cat profiles";
    }
  }

  return null;
};

export const DEFAULT_WHATSAPP_PET_CARE_MESSAGE =
  "Hi, I would like to chat about your Pet Care on your page https://app.luce.com";

export const hasSelectedPetCondition = (conditions?: PetProfileFormData["special_conditions"]) =>
  (conditions ?? []).some((condition) => condition.selected);

export const PET_CONDITION_BLOCK_TITLE = "Let's get your pet ready first";
export const PET_CONDITION_BLOCK_DESCRIPTION =
  "Because your pet has special conditions, please chat with our care consultants on WhatsApp before booking. This helps us match the right groomer and avoid any issues on the day.";

export const PET_CONDITION_SAVED_TITLE = "Pet profile saved";
